import { ArrowLeft01Icon } from "@hugeicons/core-free-icons";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { AppLink } from "@/components/common/app-link";
import { EnvironmentBadge, Tag } from "@/components/common/badges";
import { CopyField } from "@/components/common/copy-button";
import { Icon } from "@/components/common/icon";
import { Field, Page, PageTitle, Panel } from "@/components/common/page";
import { PageSkeleton } from "@/components/common/states";
import { Button } from "@/components/ui/button";
import { DeliveriesTable } from "@/components/webhooks/deliveries-table";
import { q } from "@/data/queries";
import {
  formatDateTime,
  formatLatency,
  formatNumber,
  relativeTime,
} from "@/lib/format";
import { projectPath } from "@/lib/nav";

export const Route = createFileRoute("/p/$project/webhooks/$webhookId")({
  loader: async ({ context, params }) => {
    const webhook = await context.queryClient.ensureQueryData(
      q.webhook(params.webhookId),
    );
    if (!webhook) throw notFound();
    await context.queryClient.ensureQueryData(
      q.deliveries({ webhookId: webhook.id, limit: 200 }),
    );
    return { webhook };
  },
  pendingComponent: PageSkeleton,
  component: WebhookPage,
});

function WebhookPage() {
  const { project } = Route.useParams();
  const { webhook } = Route.useLoaderData();
  const { data: deliveries } = useSuspenseQuery(
    q.deliveries({ webhookId: webhook.id, limit: 200 }),
  );

  return (
    <Page wide>
      <Button
        variant="ghost"
        size="sm"
        className="-ml-2 w-fit text-muted-foreground"
        asChild
      >
        <AppLink to={projectPath(project, "/webhooks")}>
          <Icon icon={ArrowLeft01Icon} className="size-3.5" />
          Webhooks
        </AppLink>
      </Button>

      <PageTitle
        title={webhook.url.replace("https://", "")}
        meta={
          <>
            <EnvironmentBadge environment={webhook.environment} />
            <Tag mono>{webhook.events.length} events</Tag>
            {webhook.health.failingSince ? (
              <Tag className="border-stop/30 bg-stop-soft/70 text-stop">
                Failing since {relativeTime(webhook.health.failingSince)}
              </Tag>
            ) : null}
          </>
        }
      />

      {webhook.health.failingSince ? (
        <div
          role="alert"
          className="rounded-lg border border-stop/30 bg-stop-soft/40 px-4 py-3.5"
        >
          <h2 className="text-[13.5px] font-medium text-stop">
            This endpoint has been failing for{" "}
            {relativeTime(webhook.health.failingSince).replace(" ago", "")}
          </h2>
          <p className="mt-1 max-w-[76ch] text-[12.5px] leading-[1.55] text-ink-soft">
            {formatNumber(webhook.health.failed)} deliveries exhausted their retries,
            each timing out at ten seconds. They are held for seven days from the event,
            so replaying them once the endpoint recovers loses nothing.
          </p>
        </div>
      ) : null}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex min-w-0 flex-col gap-3">
          <DeliveriesTable deliveries={deliveries.items} />
        </div>

        <div className="flex flex-col gap-4">
          <Panel title="Endpoint" bodyClassName="px-4 py-2">
            <dl>
              <Field label="URL">
                <span className="font-mono text-[12px] break-all">{webhook.url}</span>
              </Field>
              <Field label="Created">{formatDateTime(webhook.createdAt)}</Field>
              <Field label="Delivered">{formatNumber(webhook.health.delivered)}</Field>
              <Field label="Median">{formatLatency(webhook.health.p50Ms)}</Field>
            </dl>
          </Panel>

          <Panel
            title="Signing secret"
            description="Verify every request with this before you trust it"
            bodyClassName="flex flex-col gap-2 p-4"
          >
            <CopyField value={`${webhook.secretPrefix}${"•".repeat(24)}`} />
            <p className="text-[11.5px] leading-[1.5] text-muted-foreground">
              Aux0 signs the raw body with HMAC-SHA256 and sends it in{" "}
              <span className="font-mono">aux0-signature</span>, with a timestamp to
              reject replays.
            </p>
          </Panel>

          <Panel title="Subscribed events" bodyClassName="flex flex-wrap gap-1.5 p-4">
            {webhook.events.map((event) => (
              <Tag key={event} mono>
                {event}
              </Tag>
            ))}
          </Panel>
        </div>
      </div>
    </Page>
  );
}
